import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useHeader } from "../../Providers/HeaderContextProvider";
import amplitudeService from "../../Services/amplitude";
import { getPublicInvitation } from "../../Services/shareAris";
import arisLogo from "../../Assets/icon/aris_logo.svg";
import "../Login/AuthPages.css";

// Public, unauthenticated recipient landing for a Share Aris invitation.
// Matches the AuthPages theme (white background, blue #0D5EAF title/button,
// Aris logo) used by GuidedPlanBlockPage. The account flow lives in auth
// (/login/email); the backend auto-activates once the invited email/phone
// completes signup, so the CTA just routes into that flow.

export default function ShareInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setHeaderContent } = useHeader();
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setHeaderContent(false);
    return () => setHeaderContent(true);
  }, [setHeaderContent]);

  useEffect(() => {
    let active = true;
    (async () => {
      const { invitation, error } = await getPublicInvitation(token);
      if (!active) return;
      setInvitation(invitation);
      setError(error);
      setLoading(false);
      try {
        amplitudeService.track("share_aris_opened", {
          source: "invitation_landing",
          benefit_type: invitation?.benefit_type,
        });
      } catch (_) {}
    })();
    return () => { active = false; };
  }, [token]);

  const goSignup = () => navigate(`/login/email?share_token=${encodeURIComponent(token)}`);
  const goLogin = () => navigate("/login");

  if (loading) {
    return (
      <div className="auth-page-container">
        <div className="guided-plan-block-content">
          <img src={arisLogo} alt="Aris" className="auth-page-logo" />
          <p className="guided-plan-subtitle">Loading your invitation…</p>
        </div>
      </div>
    );
  }

  const expired = error?.status === 404 || !invitation || invitation.status === "expired";

  if (expired) {
    return (
      <div className="auth-page-container">
        <div className="guided-plan-block-content">
          <img src={arisLogo} alt="Aris" className="auth-page-logo" />
          <h1 className="guided-plan-title">This invitation has expired</h1>
          <p className="guided-plan-subtitle">
            The link is no longer valid. You can still create your own Aris account anytime.
          </p>
          <button className="guided-plan-button" onClick={() => navigate("/login/email")}>
            Continue With Aris
          </button>
          <p className="auth-page-footer">© 2026 Aristotle, Inc. All rights reserved. Patents pending.</p>
        </div>
      </div>
    );
  }

  const isCompanion = invitation.benefit_type === "companion";
  const inviter = invitation.inviter_name || "Someone";
  const minutes = invitation.lifetime_minutes ?? 5;
  const days = invitation.duration_days ?? 120;

  return (
    <div className="auth-page-container">
      <div className="guided-plan-block-content">
        <img src={arisLogo} alt="Aris" className="auth-page-logo" />

        <h1 className="guided-plan-title">
          {isCompanion ? "You've received a Companion Membership" : "You've received an Aris Pass"}
        </h1>

        <p className="guided-plan-subtitle">
          {inviter} invited you to Aris. You'll get your own private Aris account with
          text conversations, {minutes} voice minutes, Connections, and Aris Dialogues for Two
          {isCompanion ? " — a renewable membership." : ` for ${days} days.`}
          <br /><br />
          {inviter} will never see your conversations, activity, or personal information.
        </p>

        <button className="guided-plan-button" onClick={goSignup}>
          {isCompanion ? "Activate Membership" : "Activate Aris Pass"}
        </button>

        <button
          onClick={goLogin}
          style={{
            background: "none", border: "none", color: "#0D5EAF", cursor: "pointer",
            fontFamily: "Urbanist, sans-serif", fontSize: "16px", fontWeight: 600,
          }}
        >
          I Already Use Aris
        </button>

        <p className="auth-page-footer">© 2026 Aristotle, Inc. All rights reserved. Patents pending.</p>
      </div>
    </div>
  );
}
